import React, { useState, useEffect, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import { API_BASE, fetchCsrfToken } from './adminShared';
import './PublicApiTokens.css';

// Distinct from FieldTokens.js — different endpoint, different visual style,
// different warning language. This is the READ-ONLY public data API, not the
// crew field-tool sync path.

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function CreatePublicTokenModal({ show, onClose, onCreated }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE}/admin/public-tokens/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        body: JSON.stringify({ label: label.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create token');
      onCreated(data);
      setLabel('');
    } catch (e2) {
      setErr(e2.message || 'Failed to create token');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className="pt-modal-header">
        <Modal.Title className="fs-6">🌐 New public API token</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <Alert variant="info" className="small mb-3 pt-scope-notice">
            <strong>Scope:</strong> read-only access to tree observation data
            (species, status, coordinates, DBH, height, sponsor). This token
            <strong> cannot</strong> access contact submissions, right-of-entry
            forms, volunteer signups, PII, or observation notes.
          </Alert>
          <Form.Group>
            <Form.Label>Label</Form.Label>
            <Form.Control
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Colorado DNR data feed"
              maxLength={100}
              required
              disabled={busy}
              autoFocus
            />
            <Form.Text className="text-muted">
              Human-readable identifier so you can tell tokens apart — usually the
              consumer organization or project name.
            </Form.Text>
          </Form.Group>
          {err && <Alert variant="danger" className="mt-3 mb-0">{err}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="info" size="sm" type="submit" disabled={busy || !label.trim()}>
            {busy ? 'Generating…' : 'Generate public token'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ShowOncePublicTokenModal({ payload, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!payload) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload.raw_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard blocked; user can still select+copy manually
    }
  };

  return (
    <Modal show={!!payload} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header className="pt-modal-header">
        <Modal.Title className="fs-6">
          🌐 Public API token generated for &ldquo;{payload.token.label}&rdquo;
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          <strong>Copy this token now.</strong> It is displayed exactly once and
          cannot be recovered. If lost, revoke it and generate a new one.
        </Alert>
        <div className="pt-raw-token">
          <code>{payload.raw_token}</code>
        </div>
        <div className="d-flex align-items-center gap-2 mt-3">
          <Button variant="info" size="sm" onClick={copy}>
            {copied ? 'Copied ✓' : 'Copy to clipboard'}
          </Button>
          <span className="text-muted small">
            Prefix: <code>{payload.token.token_prefix}</code>
          </span>
        </div>
        <div className="pt-usage mt-3">
          <div className="small text-muted mb-1"><strong>Usage:</strong></div>
          <pre className="pt-usage-code mb-0">
{`curl -H "Authorization: Bearer <token>" \\
     "${window.location.origin}/api/public/trees/?bbox=39,-106,41,-104"`}
          </pre>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          I&rsquo;ve copied it &mdash; dismiss
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RevokePublicTokenModal({ token, onCancel, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!token) return null;

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE}/admin/public-tokens/${token.id}/revoke/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrf },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Revoke failed');
      onConfirmed(data);
    } catch (e) {
      setErr(e.message || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={!!token} onHide={onCancel} centered>
      <Modal.Header closeButton className="pt-modal-header">
        <Modal.Title className="fs-6">Revoke public API token?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Revoke <strong>&ldquo;{token.label}&rdquo;</strong>
          {' '}(<code>{token.token_prefix}&hellip;</code>)?
        </p>
        <p className="text-muted small mb-0">
          The consumer using this token will lose read access on their next
          request. No tree data is deleted.
        </p>
        {err && <Alert variant="danger" className="mt-3 mb-0">{err}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={confirm} disabled={busy}>
          {busy ? 'Revoking…' : 'Revoke token'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function PublicApiTokens() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [showOnce, setShowOnce] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/public-tokens/`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTokens(data.tokens || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuperuserFlag = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/check-auth/`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setIsSuperuser(!!(data.user && data.user.is_superuser));
      }
    } catch {
      setIsSuperuser(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
    fetchSuperuserFlag();
  }, [fetchTokens, fetchSuperuserFlag]);

  return (
    <div className="pt-wrap">
      <Alert variant="info" className="pt-banner mb-0">
        <div className="d-flex align-items-start gap-2">
          <span className="pt-banner-icon">🌐</span>
          <div>
            <strong>Public API tokens — read-only tree data feed.</strong>
            {' '}Distinct from field-tool crew tokens. Use these for external data
            partners (research groups, GIS teams, sponsor dashboards) who need
            access to tree observation data <strong>only</strong>. No PII, no
            contact submissions, no right-of-entry data is reachable with these.
            Endpoint: <code>/api/public/trees/</code>.
          </div>
        </div>
      </Alert>

      <div className="pt-header">
        <div>
          <strong>Public API tokens</strong>
          <span className="text-muted small ms-2">
            Hashed at rest; the raw token is shown once at creation only.
          </span>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="outline-secondary" size="sm" onClick={fetchTokens} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
          </Button>
          {isSuperuser ? (
            <Button variant="info" size="sm" onClick={() => setCreateModal(true)}>
              🌐 + New public API token
            </Button>
          ) : (
            <span
              className="text-muted small"
              title="Public API token creation is restricted to superuser accounts. Ask a superuser to generate one; you can still revoke existing tokens."
            >
              🔒 Superuser required to create tokens
            </span>
          )}
        </div>
      </div>

      {tokens.length === 0 && !loading ? (
        <div className="text-center py-5 text-muted">
          No public API tokens yet. Click <strong>New public API token</strong> to
          generate one for an external consumer.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th>Label</th>
                <th>Prefix</th>
                <th>Created by</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Last IP</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className={t.is_active ? '' : 'pt-row-revoked'}>
                  <td className="fw-medium">
                    <span className="pt-scope-badge" title="Public read-only API token">🌐</span>
                    {' '}{t.label}
                  </td>
                  <td><code className="small">{t.token_prefix}&hellip;</code></td>
                  <td className="small">{t.created_by_username}</td>
                  <td className="small">{formatWhen(t.created_at)}</td>
                  <td className="small">{formatWhen(t.last_used_at)}</td>
                  <td className="small font-monospace">{t.last_used_ip || '—'}</td>
                  <td>
                    {t.is_active ? (
                      <Badge bg="info">Active</Badge>
                    ) : (
                      <Badge bg="secondary" title={`Revoked ${formatWhen(t.revoked_at)} by ${t.revoked_by_username || '?'}`}>
                        Revoked
                      </Badge>
                    )}
                  </td>
                  <td>
                    {t.is_active && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setRevoking(t)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreatePublicTokenModal
        show={createModal}
        onClose={() => setCreateModal(false)}
        onCreated={(payload) => {
          setCreateModal(false);
          setShowOnce(payload);
          fetchTokens();
        }}
      />
      <ShowOncePublicTokenModal
        payload={showOnce}
        onClose={() => setShowOnce(null)}
      />
      <RevokePublicTokenModal
        token={revoking}
        onCancel={() => setRevoking(null)}
        onConfirmed={() => {
          setRevoking(null);
          fetchTokens();
        }}
      />
    </div>
  );
}

export default PublicApiTokens;
